// Phase 16.5 — Address-first checkout page.
//
// Flow:
//   /cart → "Continue to checkout" → /checkout (this page)
//        → user enters shipping/delivery details
//        → POST /api/checkout/address  → returns checkout_id + locked totals
//        → POST /api/checkout/stripe/session with { checkout_id }
//        → redirect to Stripe Checkout
//
// Stripe NEVER sees the cart until the address is captured + totals are
// computed and stored. The totals breakdown (subtotal · shipping 8% mail ·
// delivery $8 same-day · tax 3% · total) is shown on this page BEFORE the
// "Continue to payment" button so the customer agrees to the final amount.

import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Loader2, MapPin, Truck, Mail, Plus, CheckCircle, Trash2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const CART_KEY = "latm_cart_v1";

function loadCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : { items: [] };
  } catch (_) { return { items: [] }; }
}

const EMPTY_ADDRESS = {
  full_name: "", email: "", phone: "",
  address_line1: "", address_line2: "",
  city: "", state: "", postal_code: "", country: "US",
  delivery_instructions: "",
};

export default function CheckoutAddress() {
  const [cart, setCart]               = useState({ items: [] });
  const [me, setMe]                   = useState(null);
  const [savedAddresses, setSaved]    = useState([]);
  const [selectedSaved, setSelected]  = useState(null);
  const [showNew, setShowNew]         = useState(false);
  const [address, setAddress]         = useState(EMPTY_ADDRESS);
  const [fulfillment, setFulfillment] = useState("mail_shipping");
  const [savePref, setSavePref]       = useState(true);
  const [draft, setDraft]             = useState(null);
  const [busy, setBusy]               = useState(false);
  const [stripeBusy, setStripeBusy]   = useState(false);
  const [err, setErr]                 = useState("");
  const navigate = useNavigate();

  useEffect(() => { setCart(loadCart()); }, []);
  useEffect(() => {
    api.get("/auth/me")
       .then((r) => {
         setMe(r.data);
         setAddress((a) => ({
           ...a,
           full_name: r.data?.name || a.full_name,
           email:     r.data?.email || a.email,
         }));
       })
       .catch(() => {});
    api.get("/customers/me/addresses")
       .then((r) => {
         const list = r.data?.addresses || [];
         setSaved(list);
         const def = list.find((x) => x.is_default) || list[0];
         if (def) {
           setSelected(def.id);
           setAddress({ ...EMPTY_ADDRESS, ...def.address });
         } else {
           setShowNew(true);
         }
       })
       .catch(() => { setShowNew(true); });
  }, []);

  const subtotalCents = useMemo(
    () => cart.items.reduce((acc, i) => acc + i.price_cents * i.qty, 0),
    [cart],
  );

  // Same-day is only offered when the backend reported all items eligible
  // (i.e. inside the 10-mi radius of an active Lifted location AND the
  // products were enabled for same-day by admin).
  const sameDayAvailable = !!(draft
    && draft.same_day_eligibility
    && draft.same_day_eligibility.eligible);

  // If user previously selected same-day and it's no longer available
  // (because address/cart changed), bounce them back to mail_shipping.
  useEffect(() => {
    if (!sameDayAvailable && fulfillment === "same_day_delivery") {
      setFulfillment("mail_shipping");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameDayAvailable]);

  // -- POST /api/checkout/address whenever fulfillment OR address changes --
  async function recalc() {
    if (!cart.items.length) return;
    if (!isAddressValid(address)) return;
    setBusy(true); setErr("");
    try {
      const payload = {
        fulfillment_type: fulfillment,
        address,
        items: cart.items.map((i) => ({
          product_handle:      i.slug,
          qty:                 i.qty,
          variant_id:          i.variant_id || null,
          variant_title:       i.variant_title || null,
          square_variation_id: i.square_variation_id || null,
          shopify_variant_id:  i.shopify_variant_id || null,
          sku:                 i.sku || null,
          partner_id:          i.partner_id || null,
          partner_slug:        i.partner_slug || null,
          source_type:         i.source_type || null,
        })),
        save_address: savePref,
        ...(draft?.id ? { checkout_id: draft.id } : {}),
      };
      const { data } = await api.post("/checkout/address", payload);
      setDraft(data);
      // eslint-disable-next-line no-console
      console.log("CHECKOUT_STEP1_STATE", {
        addressValid:           true,
        fulfillment_requested:  fulfillment,
        fulfillment_effective:  data.fulfillment_type,
        downgraded_from_same_day: data.downgraded_from_same_day,
        subtotal_cents:         data.subtotal_cents,
        shipping_cents:         data.shipping_cents,
        delivery_fee_cents:     data.delivery_fee_cents,
        tax_cents:              data.tax_cents,
        total_cents:            data.total_cents,
        displayed_total:        data.total_amount,
        same_day_available:     !!(data.same_day_eligibility && data.same_day_eligibility.eligible),
      });
    } catch (e) {
      // 422 = same-day delivery unavailable — clear any stale draft so the
      // pay button stays gated and the user can fix their address or switch
      // to mail shipping.
      if (e?.response?.status === 422) setDraft(null);
      setErr(apiErrorMessage(e));
    } finally { setBusy(false); }
  }

  // Auto-recalc whenever the customer changes address, fulfillment, or cart
  // so they see live totals without ever needing to press "Save address".
  useEffect(() => {
    if (!cart.items.length) return;
    if (!isAddressValid(address)) return;
    const t = setTimeout(() => { recalc(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address.address_line1, address.city, address.state, address.postal_code,
    address.email, address.phone, address.full_name,
    fulfillment, cart.items.length,
  ]);

  // -- Square handoff (Phase 24-32 Final — replaces Stripe redirect) -----
  async function goToStripe() {
    if (!isAddressValid(address)) {
      setErr("Please complete all required address fields.");
      return;
    }
    setStripeBusy(true); setErr("");
    try {
      // Always re-POST /api/checkout/address so we get the latest backend-
      // validated totals BEFORE locking. If backend returns 422 (item not
      // fulfillable for this address), surface the message and stop.
      const payload = {
        fulfillment_type: fulfillment,
        address,
        items: cart.items.map((i) => ({
          product_handle:      i.slug,
          qty:                 i.qty,
          variant_id:          i.variant_id || null,
          variant_title:       i.variant_title || null,
          square_variation_id: i.square_variation_id || null,
          shopify_variant_id:  i.shopify_variant_id || null,
          sku:                 i.sku || null,
          partner_id:          i.partner_id || null,
          partner_slug:        i.partner_slug || null,
          source_type:         i.source_type || null,
        })),
        save_address: savePref,
        ...(draft?.id ? { checkout_id: draft.id } : {}),
      };
      const { data } = await api.post("/checkout/address", payload);
      setDraft(data);
      // Lock totals — this stamps the canonical total_cents the customer pays
      await api.post("/checkout/create", { checkout_id: data.id });

      // Iter89 — Branch on active processor. Shopify is the default when
      // SHOPIFY_CHECKOUT_ACTIVE=true; Square remains as rollback.
      let processor = { shopify_checkout_active: false, square_checkout_active: true };
      try {
        const { data: p } = await api.get("/checkout/processor");
        processor = p || processor;
      } catch (e) { /* fall back to Square if flags endpoint unreachable */ }

      if (processor.shopify_checkout_active) {
        try {
          const { data: sh } = await api.post("/shopify/checkout/create", {
            checkout_id: data.id,
            customer_email: address.email,
          });
          if (sh?.ok && sh?.checkout_url) {
            window.location.assign(sh.checkout_url);
            return;
          }
          if (sh?.error === "variant_missing") {
            const titles = (sh.blocked_items || [])
              .map((b) => b.title || b.handle).filter(Boolean).join(", ");
            // Customer-friendly copy per Iter91 task 8.
            setErr(
              "This item is temporarily unavailable for checkout. " +
              "Please remove it or try again shortly." +
              (titles ? ` (${titles})` : "")
            );
            return;
          }
          setErr(sh?.customer_message
                  || sh?.error
                  || "We could not start the Shopify checkout. Please try again.");
          return;
        } catch (e) {
          // Surface backend message (e.g. shopify_checkout_disabled) and fall
          // through to Square only if Square is still active.
          const detail = e?.response?.data;
          if (detail?.customer_message) { setErr(detail.customer_message); return; }
          if (!processor.square_checkout_active) {
            setErr(apiErrorMessage(e));
            return;
          }
          // else fall through to Square as rollback
        }
      }

      // Square fallback (rollback path)
      window.location.assign(
        `/checkout/pay?checkout_id=${data.id}&email=${encodeURIComponent(address.email)}`
      );
      return;
    } catch (e) {
      if (e?.response?.status === 422) setDraft(null);
      setErr(apiErrorMessage(e));
    } finally { setStripeBusy(false); }
  }

  // -- Saved address picker handler --------------------------------------
  function selectSaved(id) {
    setSelected(id);
    setShowNew(false);
    const row = savedAddresses.find((s) => s.id === id);
    if (row) {
      setAddress({ ...EMPTY_ADDRESS, ...row.address });
    }
  }

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="checkout-address-empty">
        <h1 className="font-anton text-3xl uppercase">No items to checkout</h1>
        <Link to="/shop" className="btn-lifted mt-4 inline-flex" data-testid="ca-back-shop">Back to Shop</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="checkout-address-page">
      <header className="mb-6">
        <div className="eyebrow flex items-center gap-2">
          <ShoppingBag className="h-3 w-3" /> Checkout · Step 1 of 2
        </div>
        <h1 className="font-anton text-3xl sm:text-4xl uppercase mt-2">
          Where should we send it?
        </h1>
        <p className="text-lifted-dim mt-2 text-sm">
          We need your name, contact, and destination before payment. Card is processed securely by Square on the next step.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* LEFT — Address form */}
        <div className="space-y-4">
          {savedAddresses.length > 0 && (
            <section className="glass-panel p-4" data-testid="ca-saved-addresses">
              <div className="font-bebas text-[0.7rem] tracking-[0.22em] uppercase text-lifted-dim mb-2">
                Saved addresses
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {savedAddresses.map((s) => (
                  <button key={s.id} onClick={() => selectSaved(s.id)}
                          className={`text-left p-3 rounded border ${selectedSaved === s.id ? "border-lifted-green/60 bg-lifted-green/5" : "border-white/10"}`}
                          data-testid={`ca-saved-${s.id}`}>
                    <div className="text-xs font-bebas tracking-[0.18em] uppercase text-lifted-green">
                      {s.label}{s.is_default ? " · Default" : ""}
                    </div>
                    <div className="text-sm text-white">{s.address.full_name}</div>
                    <div className="text-xs text-lifted-dim mt-0.5">
                      {s.address.address_line1}, {s.address.city} {s.address.state} {s.address.postal_code}
                    </div>
                  </button>
                ))}
                <button onClick={() => { setShowNew(true); setSelected(null); setAddress({ ...EMPTY_ADDRESS, email: me?.email || "", full_name: me?.name || "" }); }}
                        className="text-left p-3 rounded border border-dashed border-white/15 text-lifted-dim hover:border-lifted-green/40 hover:text-white"
                        data-testid="ca-new-address-btn">
                  <Plus className="h-3 w-3 inline mr-1" /> New address
                </button>
              </div>
            </section>
          )}

          {(showNew || savedAddresses.length === 0) && (
            <section className="glass-panel p-4" data-testid="ca-address-form">
              <div className="font-bebas text-[0.7rem] tracking-[0.22em] uppercase text-lifted-green mb-3">
                Shipping / delivery details
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input label="Full name" value={address.full_name}
                       onChange={(v) => setAddress({ ...address, full_name: v })}
                       required testid="ca-full-name" />
                <Input label="Email" type="email" value={address.email}
                       onChange={(v) => setAddress({ ...address, email: v })}
                       required testid="ca-email" />
                <Input label="Phone" type="tel" value={address.phone}
                       onChange={(v) => setAddress({ ...address, phone: v })}
                       required testid="ca-phone" />
                <Input label="Country" value={address.country}
                       onChange={(v) => setAddress({ ...address, country: v })}
                       testid="ca-country" />
                <Input label="Address line 1" wide value={address.address_line1}
                       onChange={(v) => setAddress({ ...address, address_line1: v })}
                       required testid="ca-line1" />
                <Input label="Address line 2 (optional)" wide value={address.address_line2}
                       onChange={(v) => setAddress({ ...address, address_line2: v })}
                       testid="ca-line2" />
                <Input label="City" value={address.city}
                       onChange={(v) => setAddress({ ...address, city: v })}
                       required testid="ca-city" />
                <Input label="State" value={address.state}
                       onChange={(v) => setAddress({ ...address, state: v.toUpperCase() })}
                       required testid="ca-state" />
                <Input label="ZIP / Postal code" value={address.postal_code}
                       onChange={(v) => setAddress({ ...address, postal_code: v })}
                       required testid="ca-zip" />
                <Input label="Delivery instructions (optional)" wide
                       value={address.delivery_instructions}
                       onChange={(v) => setAddress({ ...address, delivery_instructions: v })}
                       testid="ca-notes" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input type="checkbox" id="save-address" checked={savePref}
                       onChange={(e) => setSavePref(e.target.checked)}
                       data-testid="ca-save-address-pref" />
                <label htmlFor="save-address" className="text-xs text-lifted-dim">
                  Save this address to my account for next time
                </label>
              </div>
            </section>
          )}

          {/* Fulfillment selector — only show same-day if cart is genuinely eligible */}
          <section className="glass-panel p-4" data-testid="ca-fulfillment-section">
            <div className="font-bebas text-[0.7rem] tracking-[0.22em] uppercase text-lifted-green mb-3">
              How should we get it to you?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FulfillmentOption
                icon={Mail} active={fulfillment === "mail_shipping"}
                title="Mail Shipping" subtitle="Standard $5.99 · Expedited $12.99"
                onClick={() => setFulfillment("mail_shipping")}
                testid="ca-fulfillment-mail" />
              {sameDayAvailable && (
                <FulfillmentOption
                  icon={Truck} active={fulfillment === "same_day_delivery"}
                  title="Same-Day Delivery" subtitle="Lifted Supplies · $8 delivery fee"
                  onClick={() => setFulfillment("same_day_delivery")}
                  testid="ca-fulfillment-sameday" />
              )}
            </div>
            {!sameDayAvailable && (
              <p className="text-[10px] text-lifted-dim mt-3" data-testid="ca-sameday-unavailable">
                Same-day delivery isn&apos;t available for your address or for the items in your cart.
              </p>
            )}
          </section>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={recalc}
              disabled={busy || !isAddressValid(address)}
              className="btn-lifted-ghost disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="ca-save-address">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
              {draft ? "Update address" : "Save address & calculate totals"}
            </button>
          </div>
          {err && <div className="text-rose-300 text-sm" data-testid="ca-error">{err}</div>}
        </div>

        {/* RIGHT — Totals summary */}
        <aside className="glass-panel p-4 lg:sticky lg:top-6" data-testid="ca-totals-panel">
          <div className="font-bebas text-[0.7rem] tracking-[0.22em] uppercase text-lifted-green mb-3">
            Order summary
          </div>
          <ul className="space-y-1.5 mb-4">
            {cart.items.map((i) => {
              const key = i.cart_key || i.slug;
              return (
                <li key={key} className="flex justify-between text-xs" data-testid={`ca-line-${key}`}>
                  <span className="truncate min-w-0">
                    {i.qty}× {i.title}
                    {i.variant_title && (
                      <span className="block text-[10px] text-lifted-green font-bebas tracking-[0.18em] uppercase" data-testid={`ca-variant-${key}`}>
                        Variant: {i.variant_title}
                      </span>
                    )}
                  </span>
                  <span className="text-white shrink-0 ml-2">${((i.price_cents * i.qty) / 100).toFixed(2)}</span>
                </li>
              );
            })}
          </ul>
          <SummaryLine label="Subtotal"
                       value={`$${(subtotalCents / 100).toFixed(2)}`}
                       testid="ca-sum-subtotal" />
          {draft ? (
            <>
              {draft.shipping_fee > 0 && (
                <SummaryLine label={`Shipping${draft.shipping_option === 'expedited' ? ' (expedited)' : ''}`}
                             value={`$${draft.shipping_fee.toFixed(2)}`}
                             testid="ca-sum-shipping" />
              )}
              {draft.delivery_fee > 0 && (
                <SummaryLine label="Same-day delivery"
                             value={`$${draft.delivery_fee.toFixed(2)}`}
                             testid="ca-sum-delivery" />
              )}
              {draft.tax_fee > 0 && (
                <SummaryLine label={`Tax${draft.tax_state ? ` (${draft.tax_state})` : ''}`}
                             value={`$${draft.tax_fee.toFixed(2)}`}
                             testid="ca-sum-tax" />
              )}
              <hr className="border-white/10 my-2" />
              <div className="flex justify-between items-baseline" data-testid="ca-sum-total">
                <span className="font-bebas tracking-[0.18em] uppercase text-xs">Total</span>
                <span className="font-anton text-2xl text-lifted-green">
                  ${draft.total_amount.toFixed(2)} USD
                </span>
              </div>
            </>
          ) : (
            <div className="text-xs text-lifted-dim italic" data-testid="ca-sum-pending">
              {busy
                ? "Calculating shipping + tax…"
                : isAddressValid(address)
                  ? "Calculating totals…"
                  : "Enter your shipping address to see the final total."}
            </div>
          )}

          <button
            type="button"
            onClick={goToStripe}
            disabled={!isAddressValid(address) || stripeBusy || busy}
            className="btn-lifted w-full mt-5 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="ca-continue-stripe">
            {stripeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {stripeBusy ? "Loading secure checkout…" : "Continue to payment →"}
          </button>
          <p className="text-[0.6rem] text-lifted-dim mt-2 leading-relaxed text-center">
            Secure card processing by Square · Card · Cash App Pay
          </p>
        </aside>
      </div>
    </div>
  );
}

function isAddressValid(a) {
  return ["full_name", "email", "phone", "address_line1", "city", "state", "postal_code"]
    .every((k) => (a[k] || "").trim().length > 0);
}

function Input({ label, value, onChange, type = "text", required, wide, testid }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="block text-[0.6rem] tracking-[0.18em] uppercase font-bebas text-lifted-dim mb-1">
        {label}{required && <span className="text-rose-400"> *</span>}
      </label>
      <input
        type={type} value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="input-lifted w-full text-sm"
        data-testid={testid}
        required={required}
      />
    </div>
  );
}

function FulfillmentOption({ icon: Icon, active, title, subtitle, onClick, testid }) {
  return (
    <button type="button" onClick={onClick}
            className={`text-left p-3 rounded border transition ${
              active ? "border-lifted-green/60 bg-lifted-green/5" : "border-white/10 hover:border-white/30"
            }`}
            data-testid={testid}>
      <Icon className={`h-4 w-4 mb-1 ${active ? "text-lifted-green" : "text-lifted-dim"}`} />
      <div className="font-bebas tracking-[0.18em] text-sm uppercase text-white">{title}</div>
      <div className="text-xs text-lifted-dim">{subtitle}</div>
    </button>
  );
}

function SummaryLine({ label, value, testid }) {
  return (
    <div className="flex justify-between items-baseline text-sm" data-testid={testid}>
      <span className="text-lifted-dim text-xs">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
